import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { moveItem } from "components/dragReorder";
import { ReorderableCheckList } from "components/ReorderableCheckList";
import { Column } from "components/Table";
import { useState } from "react";

// NS-731: moved out of Activity/index.tsx; the list, drag-reorder and ghost
// now come from ReorderableCheckList (shared with FilterSelector).

export interface ColumnSelectorProps<K extends string> {
    tableDensity: "medium" | "small"
    columns: ColumnSelectorDef<K>[]
    currentState: Column<K>[]
    onSave: (s: Column<K>[], tableDensity: "medium" | "small") => void
    onReset: () => void
}

export interface ColumnSelectorDef<K> {
    columnId: K
    displayName: string
}

interface ColumnSelectorItemState<K> extends Column<K> {
    enabled: boolean
}

export function ColumnSelector<K extends string>(props: ColumnSelectorProps<K>) {
    const [open, setOpen] = useState(false);
    const [currentColumnState, setCurrentColumnState] = useState<ColumnSelectorItemState<K>[]>([]);
    const [tableDensity, setTableDensity] = useState(props.tableDensity || "medium");


    const updateState = () => {
        setCurrentColumnState(
            props.currentState.map(x => {
                return {
                    ...x,
                    enabled: true,
                };
            }).concat(
                props.columns.filter(x => {
                    return props.currentState.find(y => y.columnId === x.columnId) === undefined;
                }).map(x => {
                    return {
                        ...x,
                        enabled: false,
                    };
                })
            )
        );
    };

    const clicked = () => {
        if (!open) {
            updateState(); //unecessary?
            setOpen(true);
        } else {
            setOpen(false);
        }
    };

    const save = () => {
        props.onSave(currentColumnState.filter(x => x.enabled === true), tableDensity);
        setOpen(false);
    };

    return <ReorderableCheckList
        buttonLabel="View"
        open={open}
        onButtonClick={clicked}
        onClose={() => setOpen(false)}
        header={<>
            <FormControl>
                <FormLabel id="row-radio-buttons-group-label">Table Density</FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={tableDensity}
                    onChange={(e) => setTableDensity(e.target.value as "medium" | "small")}
                >
                    <FormControlLabel value="medium" control={<Radio />} label="Medium" />
                    <FormControlLabel value="small" control={<Radio />} label="Compact" />
                </RadioGroup>
            </FormControl>
            <Typography>Column Settings</Typography>
        </>}
        items={currentColumnState.map(x => ({
            label: props.columns.find(y => y.columnId === x.columnId)?.displayName || "",
            checked: x.enabled,
            disabled: x.sortOrder !== undefined,
        }))}
        onToggle={(idx) => {
            const toggled = currentColumnState[idx];
            setCurrentColumnState(currentColumnState.map(y => {
                if (y.columnId === toggled.columnId) {
                    return {
                        ...y,
                        enabled: !y.enabled,
                    };
                }
                return y;
            }));
        }}
        onReorder={(from, to) => {
            const newColumns = currentColumnState.map(x => {
                return { ...x } as ColumnSelectorItemState<K>;
            });//do a deep copy of the state array

            moveItem(newColumns, from, to);
            setCurrentColumnState(newColumns);
        }}
        footer={
            <Stack sx={{ marginTop: "10px" }} direction="row">
                <Button sx={{ marginLeft: "auto" }} variant="outlined" onClick={() => { props.onReset(); setOpen(false); }}>Reset</Button>
                <Button sx={{ marginLeft: "5px" }} variant="outlined" onClick={() => setOpen(false)}>Cancel</Button>
                <Button sx={{ marginLeft: "5px" }} variant="contained" onClick={save}>Apply</Button>
            </Stack>
        }
    />;
}
